import { useQuery } from '@tanstack/react-query';
import { reportApi, orderApi } from '../../api/services';
import { TrendingUp, ShoppingBag, Users, XCircle, DollarSign, BarChart2 } from 'lucide-react';
import { maskKvkkName } from '../../utils/kvkk';
import './AdminDashboard.css';

export default function AdminDashboard() {
  const today = new Date();
  const fromDate = new Date(today);
  fromDate.setDate(today.getDate() - 30);

  const { data: summary } = useQuery({
    queryKey: ['report-summary'],
    queryFn: () => reportApi.getSummary({ fromDate: fromDate.toISOString(), toDate: today.toISOString() }).then(r => r.data),
  });

  const { data: topProducts } = useQuery({
    queryKey: ['top-products'],
    queryFn: () => reportApi.getTopProducts({ fromDate: fromDate.toISOString(), toDate: today.toISOString(), top: 5 }).then(r => r.data),
  });

  const { data: recentOrders } = useQuery({
    queryKey: ['recent-orders'],
    queryFn: () => orderApi.getAll({ pageSize: 5 }).then(r => r.data),
    refetchInterval: 30_000,
  });

  const stats = [
    { label: 'Toplam Üye', value: summary?.totalUsers ?? '-', icon: Users, color: '#8b5cf6' },
    { label: 'Toplam Sipariş', value: summary?.totalOrders ?? '-', icon: ShoppingBag, color: '#3b82f6' },
    { label: 'Toplam Gelir', value: summary ? `₺${summary.totalRevenue?.toFixed(0)}` : '-', icon: DollarSign, color: '#10b981' },
    { label: 'Ort. Sepet', value: summary ? `₺${summary.averageOrderValue?.toFixed(0)}` : '-', icon: TrendingUp, color: '#f59e0b' },
    { label: 'İptal Oranı', value: summary ? `%${summary.cancellationRate}` : '-', icon: XCircle, color: '#ef4444' },
  ];

  const orderStatusMap: Record<string, { label: string; color: string }> = {
    'Pending': { label: 'Bekliyor', color: '#f59e0b' },
    'Confirmed': { label: 'Onaylandı', color: '#3b82f6' },
    'Preparing': { label: 'Hazırlanıyor', color: '#8b5cf6' },
    'WaitingCourier': { label: 'Kurye Bekl.', color: '#f97316' },
    'CourierAssigned': { label: 'Kurye At.', color: '#06b6d4' },
    'OnTheWay': { label: 'Yolda', color: '#10b981' },
    'Delivered': { label: 'Teslim Ed.', color: '#22c55e' },
    'Cancelled': { label: 'İptal', color: '#ef4444' },
  };

  return (
    <div className="dashboard">
      {/* Period */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Dashboard</h1>
          <p className="dashboard-period">Son 30 gün · {fromDate.toLocaleDateString('tr-TR')} - {today.toLocaleDateString('tr-TR')}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="stat-card">
              <div className="stat-icon" style={{ background: `${stat.color}22`, color: stat.color }}>
                <Icon size={22} />
              </div>
              <div className="stat-body">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-grid">
        {/* Recent Orders */}
        <div className="card dashboard-card">
          <div className="card-header">
            <h2>Son Siparişler</h2>
            <a href="/admin/orders" className="card-link">Tümünü Gör →</a>
          </div>
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Sipariş No</th>
                  <th>Müşteri</th>
                  <th>Tutar</th>
                  <th>Durum</th>
                  <th>Tarih</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders?.items?.map((order: any) => {
                  const status = orderStatusMap[order.status] ?? { label: order.status, color: '#666' };
                  return (
                    <tr key={order.id}>
                      <td><span className="order-num">{order.orderNumber}</span></td>
                      <td style={{ fontWeight: 600 }}>{maskKvkkName(order.customerName)}</td>
                      <td className="amount">₺{order.totalAmount?.toFixed(2)}</td>
                      <td>
                        <span className="status-chip" style={{ background: `${status.color}22`, color: status.color }}>
                          {status.label}
                        </span>
                      </td>
                      <td className="date">{new Date(order.createdAt).toLocaleString('tr-TR')}</td>
                    </tr>
                  );
                })}
                {(!recentOrders?.items?.length) && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '24px' }}>Sipariş bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Products */}
        <div className="card dashboard-card">
          <div className="card-header">
            <h2>En Çok Satanlar</h2>
            <BarChart2 size={18} color="var(--color-text-secondary)" />
          </div>
          <div className="top-products">
            {topProducts?.map((p: any, i: number) => (
              <div key={p.productId} className="top-product-item">
                <span className="rank">{i + 1}</span>
                <div className="product-info">
                  <span className="product-name">{p.productName}</span>
                  <span className="product-stats">{p.totalQuantity} adet · ₺{p.totalRevenue?.toFixed(0)}</span>
                </div>
                <div className="product-bar">
                  <div
                    className="product-bar-fill"
                    style={{ width: `${(p.totalQuantity / (topProducts[0]?.totalQuantity || 1)) * 100}%` }}
                  />
                </div>
              </div>
            ))}
            {(!topProducts?.length) && (
              <p style={{ color: 'var(--color-text-secondary)', textAlign: 'center', padding: '24px' }}>Veri bulunamadı</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
